import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faClock,
    faCalendarCheck,
    faUsersGear,
    faFileInvoiceDollar,
    faUserShield,
    faMobileScreen,
    faLayerGroup,
    faEye,
    faChartSimple,
    faCheckCircle,
    faGlobe,
    faChartLine
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
    "../images/react.png",
    "../images/azure.svg",
    "../images/azure-sql.png",
    "../images/nodejs.png",
    "../images/csharp.png",
];

const Timesheet = () => {
    return (
        <>
            <Helmet>
                <title>JJ Timesheet Case Study | Timesheet & Attendance Software by Data Repo</title>
                <meta
                    name="description"
                    content="Discover how Data Repo developed JJ Timesheet, a workforce timesheet and attendance system that streamlines payroll, scheduling, and time tracking for businesses."
                />
            </Helmet>

            <section
                style={{
                    backgroundImage: `url(../images/bg-casestudies-box.png)`,
                    backgroundSize: "100% 80vh"
                }}
                className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
                <h1 className="text-3xl md:text-6xl font-bold uppercase">JJ Timesheet</h1>
                <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
                    Employee Timesheet & Attendance Software by Data Repo
                </p>
            </section>

            <section className="bg-gray-100 py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="text-3xl font-bold mb-6">Simplifying Workforce Time Management</h2>
                        <p className="text-lg text-gray-700 mb-4">
                            Managing employee attendance, payroll hours, and schedules manually can lead
                            to errors, delays, and administrative overload.
                        </p>
                        <p className="text-lg text-gray-700">
                            JJ Timesheet was developed by Data Repo to provide a centralized system for tracking
                            employee work hours, attendance, scheduling, and payroll data with accuracy and ease.
                        </p>
                    </div>
                    <img src="../images/jj-timesheet-dashboard.png" alt="Timesheet dashboard overview" className="rounded-xl shadow-lg" />
                </div>
            </section>

            <section className="bg-white py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto text-center mb-10">
                    <h2 className="text-3xl font-bold mb-4">Inside the JJ Timesheet Dashboard</h2>
                    <p className="text-gray-600 max-w-3xl mx-auto">
                        JJ Timesheet gives managers real-time insight into attendance,
                        work hours, and scheduling from a centralized dashboard.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <img src="../images/jj-timesheet.png" className="rounded-xl shadow-lg" alt="jj timesheet" />
                    <img src="../images/jj-timesheet-schedule.png" className="rounded-xl shadow-lg" alt="jj timesheet" />
                </div>
            </section>

            <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
                    <div className="flex items-center gap-4 justify-center md:justify-start">
                        <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
                            <FontAwesomeIcon icon={faGlobe} />
                        </div>
                        <div>
                            <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
                            <p className="text-lg font-semibold text-gray-800">
                                JJ Timesheet helps businesses track employee time and attendance with ease
                            </p>
                        </div>
                    </div>

                    <a
                        href="https://jjtimesheet.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
                    >
                        Visit Website →
                    </a>
                </div>
            </section>

            <section className="bg-white py-20 px-6 md:px-20">
                <div className="max-w-6xl mx-auto text-center">
                    <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
                    <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
                        JJ Timesheet was built to improve time tracking accuracy, simplify employee scheduling,
                        and streamline payroll-related attendance calculations.
                    </p>

                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faLayerGroup} className="text-3xl text-blue-700 mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Centralized Time Tracking</h3>
                            <p>Record employee work hours and attendance in one unified system.</p>
                        </div>

                        <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faEye} className="text-3xl text-blue-700 mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Operational Visibility</h3>
                            <p>Give managers real-time insight into attendance and workforce hours.</p>
                        </div>

                        <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faChartSimple} className="text-3xl text-blue-700 mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Scalable Workforce System</h3>
                            <p>Support growing teams with flexible and customizable settings.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-gray-100 py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <img src="../images/event-manual.jpg" alt="Manual timesheet process" className="rounded-xl shadow-md" />
                    <div>
                        <h2 className="text-3xl font-bold mb-6">Workforce Management Challenges</h2>
                        <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
                            <li>Manual time tracking and payroll calculations</li>
                            <li>Scheduling conflicts and poor shift visibility</li>
                            <li>Difficulty managing leave balances</li>
                            <li>Lack of real-time attendance data</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="bg-white py-16 px-6 md:px-20">
                <h2 className="text-3xl font-bold text-center mb-12">Best-in-Class Features</h2>

                <div className="grid md:grid-cols-3 gap-12 text-center">

                    <div>
                        <FontAwesomeIcon icon={faClock} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Flexible Time Entry</h3>
                        <p>Employees can log time via timers, manual entry, weekly view, or mobile devices.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faCalendarCheck} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Efficient Time Tracking</h3>
                        <p>Clock in/out, record breaks, and track work hours accurately.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Tailored Billing Rates</h3>
                        <p>Customize billing and cost rates by employee or job.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faUsersGear} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Employee Scheduling</h3>
                        <p>Create shifts, manage swaps, and ensure proper workforce coverage.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faUserShield} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Multi-Level Permissions</h3>
                        <p>Assign role-based access to ensure data security and control.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faMobileScreen} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Mobile Accessibility</h3>
                        <p>Access timesheets anytime, anywhere from mobile devices.</p>
                    </div>

                </div>
            </section>

            <section className="bg-gray-100 py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto text-center mb-10">
                    <h2 className="text-3xl font-bold mb-4">Key Features in Action</h2>
                    <p className="text-gray-600 max-w-3xl mx-auto">
                        Track time, manage leaves, schedule shifts, and access the platform from any device.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <img src="../images/jj-timesheet-reports.png" className="rounded-xl shadow-lg" alt="jj timesheet" />
                    <img src="../images/jj-timesheet-support-tickets.png" className="rounded-xl shadow-lg" alt="jj timesheet" />
                    <img src="../images/jjtimesheet-reports.png" className="rounded-xl shadow-lg" alt="jj timesheet" />
                    <img src="../images/jjtimesheet-add-new-user.png" className="rounded-xl shadow-lg" alt="jj timesheet" />
                </div>
            </section>

            <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-4xl font-bold mb-4">Business Impact</h2>
                    <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
                        Organizations using JJ Timesheet achieved improved attendance accuracy,
                        smoother payroll processing, and better workforce scheduling.
                    </p>

                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-white p-10 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-600 mb-4" />
                            <h3 className="font-semibold text-lg">Accurate Time Tracking</h3>
                        </div>
                        <div className="bg-white p-10 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-600 mb-4" />
                            <h3 className="font-semibold text-lg">Faster Payroll Processing</h3>
                        </div>
                        <div className="bg-white p-10 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faChartLine} className="text-3xl text-yellow-600 mb-4" />
                            <h3 className="font-semibold text-lg">Better Workforce Visibility</h3>
                        </div>
                    </div>
                </div>
            </section>

            <section className="flex flex-col items-center py-16 px-8">
                <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
                <div className="flex flex-wrap gap-8 justify-center">
                    {technologiesIcon.map((item, index) => (
                        <img key={index} src={item} alt="technology" width="90" />
                    ))}
                </div>
            </section>

            <ContactUsComponent />
        </>
    )
}

export default Timesheet